/**
 * Web Audio API Sound Utility for Notification Chimes & Alerts
 * 
 * Synthesizes crisp, modern notification tones with zero external dependencies
 * and zero network latency. Respects browser autoplay policies.
 */

export type SoundType = 'chime' | 'soft' | 'alert' | 'pop';

class NotificationSoundService {
    private audioCtx: AudioContext | null = null;
    private isMuted: boolean = false;
    private hasUserInteracted: boolean = false;

    constructor() {
        const saved = typeof window !== 'undefined' ? localStorage.getItem('raabta_notification_sound') : null;
        // Default to enabled (true)
        this.isMuted = saved === 'false';

        // Auto-resume AudioContext on first user interaction to satisfy browser policies
        if (typeof window !== 'undefined') {
            const handleInteraction = () => {
                this.hasUserInteracted = true;
                if (this.audioCtx && this.audioCtx.state === 'suspended') {
                    this.audioCtx.resume().catch(() => {});
                }
                window.removeEventListener('click', handleInteraction);
                window.removeEventListener('keydown', handleInteraction);
            };
            window.addEventListener('click', handleInteraction, { once: true });
            window.addEventListener('keydown', handleInteraction, { once: true });
        }
    }

    private getAudioContext(): AudioContext | null {
        try {
            if (!this.audioCtx && typeof window !== 'undefined') {
                const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
                if (AudioCtxClass) {
                    this.audioCtx = new AudioCtxClass();
                }
            }
            if (this.audioCtx && this.audioCtx.state === 'suspended') {
                this.audioCtx.resume().catch(() => {});
            }
            return this.audioCtx;
        } catch {
            return null;
        }
    }

    public isSoundEnabled(): boolean {
        return !this.isMuted;
    }

    public setSoundEnabled(enabled: boolean): void {
        this.isMuted = !enabled;
        if (typeof window !== 'undefined') {
            localStorage.setItem('raabta_notification_sound', String(enabled));
        }
    }

    public toggleSound(): boolean {
        const nextState = !this.isSoundEnabled();
        this.setSoundEnabled(nextState);
        if (nextState) {
            this.playNotificationSound('soft');
        }
        return nextState;
    }

    /**
     * Play a synthesized modern notification chime
     */
    public playNotificationSound(type: SoundType = 'chime'): void {
        if (this.isMuted) return;

        const ctx = this.getAudioContext();
        if (!ctx) return;

        const play = () => {
            try {
                const now = ctx.currentTime;
                const masterGain = ctx.createGain();
                masterGain.gain.setValueAtTime(0.65, now);
                masterGain.connect(ctx.destination);

                if (type === 'soft') {
                    // Soft high-frequency gentle blip (784Hz)
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(783.99, now); // G5
                    gain.gain.setValueAtTime(0, now);
                    gain.gain.linearRampToValueAtTime(0.6, now + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now);
                    osc.stop(now + 0.36);
                } else if (type === 'pop') {
                    // Modern bubble pop
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(520, now);
                    osc.frequency.exponentialRampToValueAtTime(920, now + 0.08);
                    gain.gain.setValueAtTime(0.7, now);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
                    osc.connect(gain);
                    gain.connect(masterGain);
                    osc.start(now);
                    osc.stop(now + 0.16);
                } else if (type === 'alert') {
                    // Double energetic tone for urgent alerts
                    [698.46, 880.0].forEach((freq, idx) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        const start = now + idx * 0.12;
                        osc.type = 'triangle';
                        osc.frequency.setValueAtTime(freq, start);
                        gain.gain.setValueAtTime(0, start);
                        gain.gain.linearRampToValueAtTime(0.75, start + 0.02);
                        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
                        osc.connect(gain);
                        gain.connect(masterGain);
                        osc.start(start);
                        osc.stop(start + 0.27);
                    });
                } else {
                    // Default 'chime' - Crisp, rich, loud dual-tone harmonic chime (G5: 784Hz -> C6: 1046.5Hz)
                    const tones = [
                        { freq: 783.99, time: 0, dur: 0.35, vol: 0.65 }, // G5
                        { freq: 1046.50, time: 0.10, dur: 0.65, vol: 0.85 } // C6 (Bright high bell)
                    ];

                    tones.forEach(({ freq, time, dur, vol }) => {
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        const start = now + time;

                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(freq, start);

                        gain.gain.setValueAtTime(0, start);
                        gain.gain.linearRampToValueAtTime(vol, start + 0.02);
                        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);

                        osc.connect(gain);
                        gain.connect(masterGain);

                        osc.start(start);
                        osc.stop(start + dur);

                        // Subtle warm harmonic overtone
                        const harmonic = ctx.createOscillator();
                        const harmGain = ctx.createGain();
                        harmonic.type = 'triangle';
                        harmonic.frequency.setValueAtTime(freq * 2, start);
                        harmGain.gain.setValueAtTime(0, start);
                        harmGain.gain.linearRampToValueAtTime(vol * 0.25, start + 0.02);
                        harmGain.gain.exponentialRampToValueAtTime(0.001, start + (dur * 0.6));
                        harmonic.connect(harmGain);
                        harmGain.connect(masterGain);
                        harmonic.start(start);
                        harmonic.stop(start + (dur * 0.65));
                    });
                }
            } catch (err) {
                console.warn('Could not play notification sound:', err);
            }
        };

        if (ctx.state === 'suspended') {
            ctx.resume().then(play).catch(() => {});
        } else {
            play();
        }
    }
}

export const notificationSound = new NotificationSoundService();
